import { useCallback } from "react";

import { Stack } from "types/generated";
import { AccessResult, NO_ACCESS_MESSAGE } from "utils/access";

import useHasStackAdminAccess from "./useHasStackAdminAccess";

/**
 * Hook that returns a function to check if the user can enable a stack.
 *
 * @returns {(stack: Stack) => AccessResult} A function that takes a stack and returns an AccessResult indicating
 * whether the user can enable the stack, with an error message if applicable.
 */
const useCanEnableStack = (): ((stack: Stack) => AccessResult) => {
  const hasStackAdminAccess = useHasStackAdminAccess();

  return useCallback(
    (stack: Stack) => {
      if (!stack.isDisabled) {
        return { result: false };
      }

      if (hasStackAdminAccess(stack)) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [hasStackAdminAccess]
  );
};

export default useCanEnableStack;
